/**
 * Which account row owns the cart for the CURRENT session?
 *
 * ONE question, ONE answer. Every storefront cart call site resolves the
 * owning account through this module rather than reading a store directly,
 * so the client and the backend cannot disagree about which row is being
 * written.
 *
 * WHY THIS EXISTS. `router/privilegedSession.js` deliberately refuses the
 * storefront shopper-token swap for every back-office user -- a Super Admin,
 * a Distributor, or anyone actively impersonating. Swapping would overwrite
 * the shared `localStorage["token"]` and tear down their session, 4xx-ing
 * their subsequent admin/impersonation calls. THAT RULE IS CORRECT AND STAYS.
 *
 * Its consequence is what this module fixes: a privileged session never gets
 * a shopper session, so Vuex `state.account` is never populated. Cart call
 * sites that read `state.account._jv.id` therefore saw `undefined` and
 * refused to act -- the reported "We couldn't find your session to add items
 * to your cart." Rather than granting the swap, we give the privileged
 * session its own cart identity: no swap needed.
 *
 * WHICH ROW IS "OWN". For an SA impersonating a distributor, the cart is the
 * SA's own, NOT the impersonated tenant's. This is not a preference; it is
 * what the backend already enforces. Impersonation is a token STAMP, not a
 * token swap (`stores/impersonation.js`): the bearer token still belongs to
 * the SA, so `g.account` inside `Account._assert_self_or_act_as`
 * (api/src/models/account.py) is the SA, and the guard passes on
 * `self.id == caller.id`. Sending the impersonated tenant's id instead would
 * lean on the `shopper.act_as` branch and write the cart onto a row the UI is
 * not reading -- a cart shown on one row and written to another.
 *
 * Jake, 2026-08-26, on this behaviour: "Having the SA have their own cart
 * makes sense, and it also matches the behavior of the user/account profile
 * page when logged in as SA and impersonating a distributor." Items added
 * while impersonating remain in the SA's cart after impersonation stops.
 *
 * RESOLUTION CHAIN, most authoritative first. It mirrors the documented
 * impersonation-aware pattern in
 * `pages/distributor/account/DistributorAccountInfo.vue`, but resolves the
 * ACTOR rather than the effective TENANT -- the cart is keyed on the account
 * row (`accounts._cart`), and `effectiveTenantId()` answers a different
 * question (which distributor), so it is deliberately NOT reused here.
 *
 *   1. Vuex `state.account._jv.id` -- a genuine shopper session, established
 *      by the token swap in StorefrontSessionGate. FIRST on purpose: this
 *      leaves the anonymous / real-shopper path byte-for-byte unchanged, so
 *      the fallbacks below can only ever ADD a previously-dead case.
 *   2. Pinia `account.accountId` -- hydrated from `/auth/login` and
 *      `/auth/me`, which return the token owner's own `account_id`. This is
 *      the actor id even mid-impersonation.
 *   3. `localStorage["userId"]` -- the durable mirror every login path writes
 *      (TheLoginPage, AcceptInvitation, AutoLogin, ...), and which
 *      impersonation start/stop never rewrites. Needed because `/store/` is
 *      in `PATHS_WITHOUT_AUTHENTICATION`, so a hard reload directly onto a
 *      storefront URL never runs the `requireToken` guard that would
 *      rehydrate Pinia.
 *
 * Returns `null` when no signal identifies an actor -- callers keep their
 * existing "we couldn't find your session" handling for that genuinely
 * sessionless case.
 */

import { currentStorefrontSlug } from "@/router/storefrontSlug";
import { heldStorefrontStoreId } from "@/router/storefrontStoreId";

/**
 * Coerce a candidate id to a positive integer, or null.
 *
 * Guards three real inputs: `localStorage` hands back strings, a cleared key
 * can read as the literal "undefined"/"null"/"", and an id of 0 is a
 * coercion accident rather than a row (account ids are positive). Returning
 * null rather than NaN keeps the caller's `== null` check meaningful.
 */
function toAccountId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function readUserIdFromStorage() {
  try {
    if (typeof localStorage?.getItem === "function") {
      return localStorage.getItem("userId");
    }
  } catch {
    // Storage unavailable (SSR, blocked cookies) -> treat as absent.
  }
  return null;
}

/**
 * Resolve the account id whose cart the current session reads and writes.
 *
 * @param {object}  options
 * @param {object}  [options.vuexStore]      The Vuex store (`this.$store`).
 * @param {number}  [options.piniaAccountId] `useAccountStore().accountId`.
 * @returns {number|null} A positive account id, or null if unresolvable.
 */
export function resolveCartAccountId({ vuexStore, piniaAccountId } = {}) {
  // 1. Genuine shopper session -- unchanged behaviour.
  const vuexId = toAccountId(vuexStore?.state?.account?._jv?.id);
  if (vuexId != null) return vuexId;

  // 2. Privileged session: the actor's own account, from Pinia.
  const piniaId = toAccountId(piniaAccountId);
  if (piniaId != null) return piniaId;

  // 3. Privileged session after a reload onto an unguarded /store/ route.
  return toAccountId(readUserIdFromStorage());
}

export default resolveCartAccountId;

/**
 * Which STORE the cart page is for -- the store id (`campaignId`).
 *
 * SUPERSEDED (2026-10-07): the cart no longer calls this. A /shop/:slug page
 * takes its store from the id StorefrontSessionGate PROVIDES
 * (`STOREFRONT_STORE_ID`, router/storefrontStoreId.js); this function's
 * storage fallback is exactly the `localStorage.campaignId` read that made an
 * impersonating Super Admin's cart request the wrong store. Kept, unused by
 * the app, pending a decision to remove it -- do not add a caller.
 *
 * The session gate resolves it from the URL slug and holds it in memory
 * (router/storefrontStoreId.js) as well as writing it to storage. Memory is
 * read FIRST: it was resolved from this page's own URL, and it survives the
 * browser dropping the storage key (WebKit, prod 2026-10-02 -- a held token
 * with no `campaignId` left the cart blank). Storage is the fallback for a
 * session the gate did not establish (a privileged session, which the gate
 * deliberately leaves alone).
 *
 * `localStorage.campaignId` can read as the literal strings "null" or
 * "undefined" (`login` writes `account.campaign_id` with no null-check);
 * both are "not resolved".
 *
 * @param {string} [slug] The store slug; defaults to the address bar's.
 * @returns {string|null}
 */
export function resolveCartCampaignId(slug = currentStorefrontSlug()) {
  const held = heldStorefrontStoreId(slug);
  if (held != null) return String(held);
  let raw = null;
  try {
    raw = localStorage.getItem("campaignId");
  } catch {
    raw = null;
  }
  if (raw == null) return null;
  const value = String(raw).trim();
  if (value === "" || value === "null" || value === "undefined") return null;
  return value;
}
